/**
 * FundReach Tab — v8.5 Pursuits Workflow
 * 3-Phase grant pursuit: Structure → Alignment Strategy → Application
 * Pursuits stored in localStorage, cross-tab "Work On" via pending key.
 * Placeholder graphics — agent backends will be wired later.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { startPursuit, getPursuitStatus, approvePursuit } from "../../services/pursueAPI";
import { useGrantCompetition } from "../../hooks/useFundMatch";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, ChevronDown, ChevronUp, Plus, CheckCircle2,
  Calendar, Target, Loader2, Zap, ArrowRight, Copy, X,
  ExternalLink, BookmarkCheck, Swords,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Tooltip, Legend,
} from "recharts";

/* ───────────── Storage ───────────── */
const DEFAULT_STORAGE_KEY = "impactplexus_pursuits_v2";
const PENDING_KEY = "impactplexus_pending_pursuit";

function seedDemoPursuits() {
  const now = new Date().toISOString();
  return [
    {
      id: "demo_uwgt", grantId: "uwgt",
      grantName: "Community Impact Fund",
      funderName: "United Way Greater Toronto",
      amountRange: "$50K–$100K", deadline: "2026-06-30",
      matchScore: 0.82, province: "GTA",
      phase: "structure", structureDone: false, alignmentDone: false,
      structureData: null, alignmentData: null, applicationData: null, addedAt: now,
    },
    {
      id: "demo_otf", grantId: "otf",
      grantName: "Youth & Community Dev. Program",
      funderName: "Ontario Trillium Foundation",
      amountRange: "$100K–$300K", deadline: "2026-05-15",
      matchScore: 0.87, province: "Ontario-wide",
      phase: "alignment", structureDone: true, alignmentDone: false,
      structureData: null, alignmentData: null, applicationData: null, addedAt: now,
    },
    {
      id: "demo_mis", grantId: "mis",
      grantName: "Community Grants Program",
      funderName: "City of Mississauga",
      amountRange: "$25K–$75K", deadline: "2026-04-01",
      matchScore: 0.76, province: "Mississauga, ON",
      phase: "application", structureDone: true, alignmentDone: true,
      structureData: null, alignmentData: null, applicationData: null, addedAt: now,
    },
  ];
}

function loadPursuits(bn, storageKey) {
  try {
    const stored = (JSON.parse(localStorage.getItem(storageKey) || "{}"))[bn];
    if (stored && stored.length > 0) return stored;
    return seedDemoPursuits();
  } catch { return seedDemoPursuits(); }
}
function savePursuits(bn, list, storageKey) {
  try {
    const all = JSON.parse(localStorage.getItem(storageKey) || "{}");
    all[bn] = list;
    localStorage.setItem(storageKey, JSON.stringify(all));
  } catch {}
}

/* ───────────── Helpers ───────────── */
function fmtAmt(v) {
  if (!v || v === "Varies") return null;
  if (typeof v === "string") return v;
  return v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : `$${Math.round(v/1000)}K`;
}
function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}
function daysTo(s) {
  if (!s) return null;
  return Math.ceil((new Date(s) - new Date()) / 86400000);
}
function dlCls(d) {
  if (d == null) return "bg-zinc-100 text-zinc-500";
  if (d <= 14) return "bg-red-100 text-red-700 font-bold";
  if (d <= 45) return "bg-amber-100 text-amber-700 font-bold";
  return "bg-emerald-100 text-emerald-700";
}

/* ───────────── Mock data builders ───────────── */
const ANALYZE_STEPS = [
  "Connected to funder database",
  "Located grant guidelines",
  "Identified required documents",
  "Deadline extracted",
  "Parsing application procedure\u2026",
  "Application procedure mapped",
  "Information requirements extracted",
  "Supplementary links gathered",
  "Compiling requirements report\u2026",
  "\u2713 Structure analysis complete",
];

function mockStructure(p) {
  const dl = fmtDate(p.deadline) || "See funder website";
  return {
    docs: [
      { label: "Annual Report (most recent)", ok: true },
      { label: "Audited Financial Statements (2 years)", ok: true },
      { label: "CRA Registered Charity letter", ok: true },
      { label: "Board of Directors list", ok: true },
      { label: "Logic Model — not yet prepared", ok: false, required: true, partnerType: "evaluation_specialist" },
    ],
    timeline: [
      { label: `Application Deadline: ${dl}`, ok: true },
      { label: "Decision: 8–10 weeks post-submission", ok: true },
      { label: "Grant period begins upon approval", ok: true },
    ],
    info: [
      { label: "CRA Registered Charity Number", ok: true },
      { label: "Annual operating budget", ok: true },
      { label: `Previous ${p.funderName} grant history — check with ED`, ok: false },
      { label: "Annual clients served (unique count) — need program data", ok: false },
      { label: "Partner support letters — 2 required", ok: false, partnerType: "community_liaison" },
    ],
    procedure: [
      { label: `Platform: ${p.funderName} grants portal`, ok: true },
      { label: "Pre-registration on portal required", ok: true },
      { label: "EOI required before full application", ok: true },
    ],
    custom: [],
    note: "⏰ Submit 2 weeks early — funder portals often congest near deadlines",
  };
}

function mockAlignment(p) {
  const ms = Math.min(0.95, Math.max(0.45, p.matchScore || 0.72));
  const s = (base, delta) => parseFloat(Math.min(10, Math.max(1, ms * 10 + delta)).toFixed(1));
  return {
    radar: {
      mission_alignment:    s(ms,  0.5),
      capacity_fit:         s(ms, -0.4),
      track_record_match:   s(ms,  0.8),
      network_proximity:    s(ms, -1.8),
      timing_window:        s(ms,  1.1),
      overall_fit_score:    s(ms,  0.0),
      recommendation: ms >= 0.70 ? "pursue" : ms >= 0.50 ? "conditional" : "defer",
      scoring_rationale: {
        mission_alignment:  "Your mandate directly addresses this funder's stated priority population and geographic focus — language overlap is strong.",
        capacity_fit:       "Organization budget and staff scale fall within this funder's typical grantee band. Reporting capacity meets their requirements.",
        track_record_match: "Previous program outcomes and evaluation history align with evidence standards this funder publicly requires.",
        network_proximity:  "Limited direct board or alumni overlap with current grantee network. Warm introduction recommended before applying.",
        timing_window:      "Grant cycle is currently active and your org is at the right growth stage — this is an optimal application moment.",
      },
    },
    proposal_map: {
      central_theme: `${p.grantName || "Community Impact Grant"} — ${p.funderName} Partnership`,
      narrative_arc: [
        `Problem: A critical service gap in ${p.province || "the region"} leaves 35% more people in need than current capacity can serve.`,
        `Solution: Our evidence-based model — backed by ${p.funderName} — extends reach by 40% with a measurably lower unit cost.`,
        "Impact: Independent evaluation confirms outcomes sustained 3 years post-funding, creating durable community infrastructure.",
      ],
      evidence_anchors: [
        { claim: "Service demand exceeds capacity by 35%", evidence: "Wait list data Q4 2025 — 847 individuals pending intake", source: "Internal program records" },
        { claim: "Model reduces unit cost by 28%", evidence: "2024 third-party evaluation comparing alternative delivery models", source: "Independent evaluation report" },
        { claim: "94% client satisfaction rate", evidence: "Annual client outcome survey, 2025 (n = 312)", source: "Client satisfaction survey" },
      ],
      impact_framing: "Frame as systems-level change — not service delivery alone, but building durable community infrastructure that outlasts the grant period.",
      format_requirements: ["LOI required before full application (3 pages)", "5-page narrative limit for full application", "Budget narrative must be a separate document", "2 years of audited financials required"],
      key_differentiators: [
        `Only organization of our type serving the 3-county catchment area`,
        "Evidence-based model with 2 peer-reviewed publications",
        "Formal Indigenous community partnership (signed MOU, active 4 years)",
        "Provincial government co-funding confirmed for operating period",
      ],
    },
    process_dag: {
      milestones: [
        { id: "m1", name: "Letters of support",      description: "3 letters: municipal government, lead partner org, community champion", depends_on: [],            estimated_days: 14, owner: "Executive Director", deliverable: "3 signed letters (PDF)" },
        { id: "m2", name: "Financial statements",    description: "Most recent 2 years audited + current year interim financials",          depends_on: [],            estimated_days:  5, owner: "Finance Manager",    deliverable: "Audited statements package" },
        { id: "m3", name: "Logic model",             description: "Theory of change diagram — outcomes hierarchy with measurable indicators", depends_on: [],            estimated_days:  7, owner: "Program Director",  deliverable: "Logic model (PDF / slide)" },
        { id: "m4", name: "Project narrative",       description: "Full narrative per funder template — problem, solution, impact, budget overview", depends_on: ["m1"], estimated_days: 10, owner: "Grant Writer",       deliverable: "5-page narrative draft" },
        { id: "m5", name: "Project budget",          description: "Detailed line-item budget with admin allocation and budget narrative",   depends_on: ["m4"],        estimated_days:  4, owner: "Finance Manager",    deliverable: "Budget spreadsheet + narrative" },
        { id: "m6", name: "Internal review",         description: "Executive Director + Board Chair sign-off on complete package",         depends_on: ["m4", "m5"],  estimated_days:  3, owner: "Executive Director", deliverable: "Signed approval memo" },
        { id: "m7", name: "Portal submission",       description: "Complete all online form fields and upload all required documents",      depends_on: ["m2","m3","m6"], estimated_days: 1, owner: "Grant Writer",     deliverable: "Submitted application (confirmation #)" },
      ],
      critical_path: ["m1", "m4", "m5", "m6", "m7"],
      total_estimated_days: 34,
      resource_requirements: ["Grant writer (20 hrs)", "Executive Director (8 hrs)", "Finance Manager (9 hrs)", "Program Director (7 hrs)"],
      risk_flags: [
        "Letters of support from government contacts can take 3–4 weeks — start this immediately.",
        "Logic model may need board input — confirm availability at next scheduled meeting.",
        "Funder portal registration can require 48-hour admin approval — register before starting the narrative.",
      ],
    },
    alignment_confidence: ms,
    recommended_next_action: ms >= 0.70
      ? "Proceed with full application — strong alignment confirmed on all critical dimensions."
      : "Conditional pursuit — address capacity and network proximity gaps before applying.",
    estimated_success_probability: parseFloat(Math.min(0.48, ms * 0.58).toFixed(2)),
    top_risks: [
      "Budget ceiling may be lower than full project cost — validate before narrative commit.",
      "LOI required — adds 3–4 weeks to timeline, adjust internal deadline accordingly.",
    ],
  };
}

function mockApplication() {
  return {
    steps: [
      { status: "done", label: "Project narrative drafted", sub: "AI proposal generated \u00b7 Reviewed by team" },
      { status: "done", label: "Budget prepared", sub: "Program/Admin split validated" },
      { status: "done", label: "Supporting documents uploaded", sub: "Annual report, financials, board list" },
      { status: "active", label: "Logic Model \u2014 in preparation", sub: "Required document not yet uploaded", ai: true },
      { status: "pending", label: "Partner letters of support", sub: "2 required \u00b7 Contact templates ready" },
      { status: "pending", label: "Final review & submit via portal", sub: "Recommended 2 weeks before deadline" },
    ],
    documents: [
      { icon: "📄", label: "Project Narrative", status: "done" },
      { icon: "📊", label: "Program Budget", status: "done" },
      { icon: "📋", label: "Annual Report", status: "done" },
      { icon: "💼", label: "Financial Statements", status: "done" },
      { icon: "🔷", label: "Logic Model", status: "active" },
      { icon: "\u2709\ufe0f", label: "Partner Support Letters", status: "pending" },
    ],
  };
}

/* ─── Map real API structure result → checklist UI format ─── */
function mapApiToStructure(apiData, pursuit) {
  try {
    const raw = apiData?.structure_result || apiData?.structure_output || apiData;
    if (!raw || typeof raw !== "object") return null;

    const ps = raw.partner_support || {};
    const ca = raw.capacity_assessment || {};
    const rl = raw.relationship_map || {};

    const docs = [
      { label: "CRA Registered Charity documentation", ok: true },
      { label: "Annual Report (most recent)", ok: true },
      { label: "Audited Financial Statements (2 years)", ok: true },
      { label: "Board of Directors list", ok: true },
    ];
    (ps.partner_requirements || []).forEach(p => {
      docs.push({
        label: `${p.partner_type?.replace(/_/g, " ") || "Specialist"}: ${p.description || "Required for application"}`,
        ok: false,
        required: ["immediate", "required"].includes(p.urgency),
        partnerType: p.partner_type,
      });
    });

    const gaps = ca.gaps || ca.capacity_gaps || [];
    const info = gaps.length > 0
      ? gaps.map(g => ({ label: typeof g === "string" ? g : g.description || String(g), ok: false }))
      : [
          { label: "Program impact metrics (unique clients served)", ok: false },
          { label: "Previous grant history with this funder", ok: false },
        ];

    const connections = rl.shared_connections || rl.connections || [];
    const procedure = [
      { label: `${pursuit.funderName} online grant portal submission`, ok: true },
      ...(connections.length > 0
        ? [{ label: `${connections.length} shared network connection(s) identified`, ok: true }]
        : []),
    ];

    return {
      docs, info, procedure,
      timeline: [
        { label: `Application Deadline: ${fmtDate(pursuit.deadline) || "See funder website"}`, ok: true },
        { label: "Decision: 6–10 weeks post-submission", ok: true },
      ],
      custom: [],
      note: ps.recommendation || raw.key_insight || "⏰ Submit 2 weeks early — portals often congest near deadlines.",
      _fromApi: true,
    };
  } catch { return null; }
}

/* ─── Tiny reusable components ─── */

const PARTNER_LABELS = {
  evaluation_specialist: "Evaluation Specialist",
  grant_writer: "Grant Writer",
  financial_auditor: "Financial Auditor",
  legal_advisor: "Legal Advisor",
  community_liaison: "Community Partner",
  project_manager: "Project Manager",
  researcher: "Researcher",
  communications_specialist: "Communications Specialist",
  technology_provider: "Technology Provider",
  other: "Specialist",
};

const FindPartnerButton = ({ partnerType }) => {
  const label = PARTNER_LABELS[partnerType] || "Specialist";
  return (
    <a
      href={`/partners?type=${partnerType}`}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-[9.5px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 px-1.5 py-0.5 rounded-full transition-colors ml-1.5 shrink-0"
      title={`Find a vetted ${label} in the ImpactPlexus partner directory`}
    >
      🤝 Find {label}
    </a>
  );
};

const CheckItem = ({ ok, required, partnerType, children }) => (
  <li className="flex items-start gap-2 text-[12px] text-zinc-700">
    {ok
      ? <span className="mt-0.5 w-[15px] h-[15px] rounded-full bg-emerald-500 flex items-center justify-center shrink-0 text-white text-[8px]">✓</span>
      : <span className="mt-0.5 w-[15px] h-[15px] rounded-full bg-amber-100 border border-amber-400 flex items-center justify-center shrink-0 text-amber-600 text-[9px]">!</span>}
    <span className="leading-snug flex flex-wrap items-center gap-1">
      {children}
      {required && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Required</span>}
      {!ok && partnerType && <FindPartnerButton partnerType={partnerType} />}
    </span>
  </li>
);

const Accordion = ({ icon, title, badge, badgeColor, defaultOpen, children }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden">
      <button className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors" onClick={() => setOpen(v => !v)}>
        <div className="w-7 h-7 rounded-md flex items-center justify-center text-sm bg-zinc-100 shrink-0">{icon}</div>
        <span className="flex-1 text-[12px] font-semibold text-zinc-800">{title}</span>
        <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full border ${badgeColor}`}>{badge}</span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-zinc-400" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 pt-2 border-t border-zinc-100">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Sidebar pursuit row ─── */

const PHASE_COLOR = { structure: "#d97706", alignment: "#7c3aed", application: "#0d9488", saved: "#0d9488" };
const PHASE_LABEL = { structure: "Structure", alignment: "Alignment", application: "Application", saved: "Saved" };
const phasePct = (p) => {
  if (p.phase === "saved") return 0;
  if (p.phase === "structure") return p.structureDone ? 100 : 0;
  if (p.phase === "alignment") return p.alignmentDone ? 100 : 65;
  return 85;
};

const PursuitRow = ({ pursuit, active, onClick }) => {
  const days = daysTo(pursuit.deadline);
  const pct = phasePct(pursuit);
  return (
    <div onClick={onClick}
      className={`mx-2 mb-1 rounded-lg p-2.5 cursor-pointer border-[1.5px] transition-all ${active ? "bg-teal-50 border-teal-200" : "border-transparent hover:bg-zinc-50"}`}>
      <div className="flex items-start justify-between gap-1 mb-0.5">
        <p className={`text-[12px] font-semibold leading-tight ${active ? "text-teal-700" : "text-zinc-800"}`}>{pursuit.grantName}</p>
        <span className="text-[10.5px] font-bold text-zinc-500 shrink-0">{pursuit.amountRange}</span>
      </div>
      <p className="text-[10.5px] text-zinc-400 mb-2">{pursuit.funderName}</p>
      <div className="flex items-center justify-between">
        {pursuit.phase === "saved" ? (
          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full border border-teal-300 text-teal-600 bg-teal-50">
            Saved · Ready to begin
          </span>
        ) : (
          <div className="flex-1 mr-2">
            <p className="text-[9.5px] text-zinc-400 mb-0.5">{PHASE_LABEL[pursuit.phase]} · {pct}%</p>
            <div className="h-[3px] bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: PHASE_COLOR[pursuit.phase] }} />
            </div>
          </div>
        )}
        {pursuit.phase !== "saved" && days != null && <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${dlCls(days)}`}>{days <= 0 ? "Overdue" : `${days}d`}</span>}
      </div>
    </div>
  );
};

/* ─── Phase tab ─── */

const PHASE_CFG = {
  structure:   { label: "Structure",          n: 1, c: { active: "border-b-amber-500 bg-amber-50",   text: "text-amber-700",  num: "bg-amber-500"  } },
  alignment:   { label: "Alignment Strategy", n: 2, c: { active: "border-b-violet-600 bg-violet-50", text: "text-violet-700", num: "bg-violet-600" } },
  application: { label: "Application",        n: 3, c: { active: "border-b-teal-600 bg-teal-50",     text: "text-teal-700",   num: "bg-teal-600"   } },
};

const PhaseTab = ({ phase, current, sDone, aDone, onClick }) => {
  const cfg = PHASE_CFG[phase];
  const done = (phase === "structure" && sDone) || (phase === "alignment" && aDone);
  const isActive = current === phase;
  const locked = (phase === "alignment" && !sDone) || (phase === "application" && !aDone);
  return (
    <button onClick={!locked ? onClick : undefined} disabled={locked}
      className={`flex-1 flex items-center gap-2 px-3 py-3 border-b-[3px] transition-all border-r border-zinc-100 last:border-r-0 ${
        isActive ? cfg.c.active :
        done     ? "border-b-teal-500 bg-white" :
        locked   ? "border-b-transparent opacity-50 cursor-not-allowed" :
                   "border-b-transparent hover:bg-zinc-50"}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black text-white shrink-0 ${done ? "bg-teal-500" : isActive ? cfg.c.num : "bg-zinc-300"}`}>
        {done ? "\u2713" : cfg.n}
      </div>
      <div className="text-left">
        <p className={`text-[12px] font-bold ${isActive ? cfg.c.text : done ? "text-teal-600" : "text-zinc-500"}`}>{cfg.label}</p>
        <p className="text-[9.5px] text-zinc-400">{done ? "Complete" : isActive ? "In progress" : locked ? "Locked 🔒" : "Not started"}</p>
      </div>
    </button>
  );
};

/* ═══════════════════════════════════════════════
   PHASE 1 — STRUCTURE
   ═══════════════════════════════════════════════ */

const StructurePhase = ({ pursuit, charityBN, orgData, onUpdate }) => {
  const [uiState, setUiState] = useState(
    pursuit.structureDone ? "done" : pursuit.runId ? "polling" : "idle"
  );
  const [log, setLog] = useState([]);
  const [progress, setProgress] = useState(0);
  const [runId, setRunId] = useState(pursuit.runId || null);
  const [apiStructure, setApiStructure] = useState(null);
  const [approving, setApproving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const logTimerRef = useRef(null);

  const { data: grantCompetition } = useGrantCompetition(
    charityBN, pursuit.grantId,
    (uiState === "done" || uiState === "hitl") && !!charityBN && !!pursuit.grantId
  );

  const sd = apiStructure || pursuit.structureData || mockStructure(pursuit);

  // Poll pursue status when runId is live
  useEffect(() => {
    if (!runId || uiState === "done" || uiState === "idle" || uiState === "hitl") return;
    const poll = setInterval(async () => {
      try {
        const status = await getPursuitStatus(runId);
        if (status.status === "hitl_wait") {
          clearInterval(poll);
          if (logTimerRef.current) clearInterval(logTimerRef.current);
          const mapped = mapApiToStructure(status.structure_result, pursuit);
          if (mapped) setApiStructure(mapped);
          setProgress(100);
          setLog(prev => [...prev, "✓ Structure analysis complete"]);
          setUiState("hitl");
        } else if (status.status === "complete") {
          clearInterval(poll);
          if (logTimerRef.current) clearInterval(logTimerRef.current);
          setProgress(100);
          onUpdate({ structureDone: true, runId });
          setUiState("done");
        } else if (status.status === "failed") {
          clearInterval(poll);
          if (logTimerRef.current) clearInterval(logTimerRef.current);
          setProgress(100);
          setLog(prev => [...prev, "⚠ Analysis used estimated data"]);
          onUpdate({ structureDone: true, structureData: mockStructure(pursuit) });
          setUiState("done");
        } else {
          setProgress(prev => Math.min(prev + 4, 88));
        }
      } catch (err) { console.warn("Pursuit poll error:", err); }
    }, 5000);
    return () => clearInterval(poll);
  }, [runId, uiState]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAnalysis = async () => {
    setUiState("running");
    setLog(["Connecting to funder database..."]);
    setProgress(8);

    try {
      const result = await startPursuit({
        entity_bn: charityBN || "demo",
        funder_id: pursuit.grantId || pursuit.funderName.replace(/\s+/g, "_").toLowerCase(),
        funder_name: pursuit.funderName,
        grant_id: pursuit.grantId || null,
        org_name: orgData?.legal_name || orgData?.name || "Your Organization",
        org_mission: orgData?.mission || "",
        org_focus_areas: orgData?.focus_areas || [],
      });
      setRunId(result.run_id);
      onUpdate({ runId: result.run_id });
      setLog(prev => [...prev, "Structure Crew activated — 18 agents analyzing funder..."]);
      setProgress(18);
      // Simulated log steps while agents run (≈ 2 min)
      let step = 0;
      logTimerRef.current = setInterval(() => {
        if (step < ANALYZE_STEPS.length - 1) {
          setLog(prev => [...prev, ANALYZE_STEPS[step]]);
          setProgress(prev => Math.min(prev + 5, 88));
          step++;
        } else { clearInterval(logTimerRef.current); }
      }, 7000);
    } catch (err) {
      console.warn("Pursue start failed, using mock:", err);
      // Graceful fallback to mock simulation
      let i = 0;
      const iv = setInterval(() => {
        if (i >= ANALYZE_STEPS.length) {
          clearInterval(iv);
          setTimeout(() => { setUiState("done"); onUpdate({ structureDone: true, structureData: mockStructure(pursuit) }); }, 400);
          return;
        }
        setProgress(Math.round(((i + 1) / ANALYZE_STEPS.length) * 100));
        setLog(prev => [...prev, ANALYZE_STEPS[i]]);
        i++;
      }, 650);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      if (runId) await approvePursuit(runId, null);
    } catch (err) { console.warn("Approve error (non-fatal):", err); }
    finally {
      setApproving(false);
      onUpdate({ structureDone: true, structureData: sd, runId, phase: "alignment" });
      setUiState("done");
    }
  };

  const addItem = () => {
    if (!newLabel.trim()) return;
    const updated = { ...sd, custom: [...(sd.custom || []), { label: newLabel.trim() }] };
    onUpdate({ structureData: updated });
    setNewLabel(""); setAdding(false);
  };

  /* idle */
  if (uiState === "idle") return (
    <div className="text-center py-12">
      <p className="text-[15px] font-bold text-zinc-700 mb-2">Start Structure Analysis</p>
      <p className="text-[12.5px] text-zinc-500 mb-6">Analyze grant guidelines, required documents, deadlines, and application procedure.</p>
      <Button className="bg-amber-500 hover:bg-amber-600 text-white px-6" onClick={runAnalysis}>
        <Target className="h-4 w-4 mr-2" /> Analyze Structure
        <span className="ml-2 text-[10px] bg-black/20 px-1.5 py-0.5 rounded-full font-bold">💎 1 credit</span>
      </Button>
    </div>
  );

  /* running or polling */
  if (uiState === "running" || uiState === "polling") return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
        <span className="font-bold text-amber-700">Gathering Structure</span>
        <span className="text-[11px] text-amber-400 ml-1">Est. 2 min</span>
      </div>
      <div className="h-2 bg-amber-100 rounded-full overflow-hidden mb-4">
        <motion.div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
      </div>
      <div className="flex flex-col gap-1.5 max-h-36 overflow-y-auto">
        <AnimatePresence>
          {log.map((msg, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-[12px] text-zinc-700">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />{msg}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );

  /* done — show accordions */
  const readyCount = sd.docs.filter(d => d.ok).length;
  const totalDocs = sd.docs.length + (sd.custom?.length || 0);
  const outstanding = sd.info.filter(i => !i.ok).length;

  /* HITL gate — review + approve before proceeding */
  const isHitl = uiState === "hitl";

  return (
    <div className="space-y-3">
      {isHitl && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-violet-500 text-lg mt-0.5">🔍</span>
          <div className="flex-1">
            <p className="text-[13px] font-bold text-violet-800 mb-0.5">Structure Analysis Ready — Review Required</p>
            <p className="text-[12px] text-violet-600">{sd._fromApi ? "Live agent data" : "Estimated data"} · Review the checklist below and approve to continue to Alignment Strategy.</p>
          </div>
        </div>
      )}
      <Accordion icon="📄" title="Required Documents" badge={`${readyCount} of ${totalDocs} ready`} badgeColor="bg-amber-50 text-amber-700 border-amber-200" defaultOpen>
        <ul className="flex flex-col gap-2 mt-2">
          {sd.docs.map((d, i) => <CheckItem key={i} ok={d.ok} required={d.required} partnerType={d.partnerType}>{d.label}</CheckItem>)}
          {(sd.custom || []).map((d, i) => (
            <CheckItem key={`c${i}`} ok={false}>
              <span className="text-amber-700">📌 {d.label}</span>
              <span className="text-[10px] text-amber-500 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Added</span>
            </CheckItem>
          ))}
        </ul>
      </Accordion>

      <Accordion icon="🗓" title="Timeline & Key Dates" badge="All clear" badgeColor="bg-teal-50 text-teal-700 border-teal-100">
        <ul className="flex flex-col gap-2 mt-2">
          {sd.timeline.map((t, i) => <CheckItem key={i} ok={t.ok}>{t.label}</CheckItem>)}
        </ul>
        {sd.note && <div className="mt-3 bg-amber-50 border-l-[3px] border-amber-400 px-3 py-2 text-[12px] text-amber-700 font-medium rounded-r-md">{sd.note}</div>}
      </Accordion>

      <Accordion icon="📋" title="Information Needed" badge={outstanding > 0 ? `${outstanding} outstanding` : "All gathered"} badgeColor={outstanding > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-teal-50 text-teal-700 border-teal-100"}>
        <ul className="flex flex-col gap-2 mt-2">
          {sd.info.map((item, i) => <CheckItem key={i} ok={item.ok} partnerType={item.partnerType}>{item.label}</CheckItem>)}
        </ul>
      </Accordion>

      <Accordion icon="🔗" title="Procedure & Links" badge="Mapped" badgeColor="bg-teal-50 text-teal-700 border-teal-100">
        <ul className="flex flex-col gap-2 mt-2">
          {sd.procedure.map((p, i) => <CheckItem key={i} ok={p.ok}>{p.label}</CheckItem>)}
        </ul>
      </Accordion>

      {/* Add other requirement or document */}
      {adding ? (
        <div className="flex gap-2 mt-1">
          <input autoFocus value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") addItem(); if (e.key === "Escape") { setAdding(false); setNewLabel(""); } }}
            placeholder="Requirement or document name\u2026"
            className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-300" />
          <Button size="sm" onClick={addItem} className="bg-amber-500 hover:bg-amber-600 text-white text-xs">Add</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewLabel(""); }}><X className="h-3.5 w-3.5" /></Button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="w-full flex items-center gap-2 px-3 py-2.5 mt-1 border-[1.5px] border-dashed border-zinc-300 rounded-lg text-[12px] text-zinc-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 transition-all">
          <Plus className="h-4 w-4" /> Add other requirement or document
        </button>
      )}

      {/* ── Funder Intelligence Brief ───────────────── */}
      <Accordion icon="🧠" title="Funder Intelligence Brief" badge="5 dimensions" badgeColor="bg-violet-50 text-violet-700 border-violet-200">
        {(() => {
          const dims = (pursuit.structureData?._dims) || [
            { icon: "🏛", label: "Org. Culture Archetype", value: "Community-First Foundation", confidence: 88, detail: "Language and portfolio consistently centre grassroots organizations with lived-experience leadership. Avoid corporate or academic framing." },
            { icon: "🏆", label: "Recognition Types",      value: "Outcomes + Ecosystem Building", confidence: 81, detail: "Funder publicly rewards measurable outcomes AND systems-level ripple effects. Both elements should appear in the narrative." },
            { icon: "⚖️",  label: "Competitive Landscape", value: grantCompetition
                ? `${grantCompetition.competition_intensity} — ${grantCompetition.mirror_plus_direct_competitors} direct competitors`
                : "Moderate — 1 in 6 funded",
              confidence: grantCompetition ? 92 : 74,
              detail: grantCompetition
                ? `${grantCompetition.mirror_plus_direct_competitors} mirror/direct competitors in your province are also eligible for this grant. ${grantCompetition.competition_intensity === 'HIGH' ? 'Differentiate strongly on outcomes, geography exclusivity, and evaluation rigour.' : grantCompetition.competition_intensity === 'MEDIUM' ? 'Moderate competition — differentiate on unique population or delivery method.' : 'Low competition — strong positioning advantage for this grant.'}`
                : "Estimated 6 applicants per award in this category. Differentiate on geography exclusivity and evaluation rigour." },
            { icon: "🕐", label: "Timing Window",         value: "Optimal — 8-week runway",      confidence: 90, detail: "Funder historically reviews in Q3. Current cycle aligns well. Submit at least 10 business days before deadline." },
            { icon: "🧵", label: "Affinity Threads",      value: "Housing · Indigenous · Youth",  confidence: 78, detail: "Portfolio analysis reveals strong affinity for housing stability, Indigenous community self-determination, and youth opportunity pathways." },
          ];
          return (
            <div className="mt-2 flex flex-col gap-2.5">
              {dims.map((d, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border border-zinc-100 rounded-xl px-3.5 py-3 shadow-sm">
                  <span className="text-lg shrink-0 mt-0.5">{d.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">{d.label}</p>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: d.confidence >= 80 ? "#f0fdf4" : "#fffbeb", color: d.confidence >= 80 ? "#15803d" : "#b45309" }}>
                        {d.confidence}% conf.
                      </span>
                    </div>
                    <p className="text-[13px] font-bold text-zinc-800 mb-1">{d.value}</p>
                    <p className="text-[11.5px] text-zinc-500 leading-snug">{d.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Accordion>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 mt-2 border-t border-zinc-100">
        <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
          <CheckCircle2 className="h-4 w-4 text-teal-500" />
          {isHitl ? "Review complete — approve to unlock Alignment" : "Structure complete — Alignment Strategy now available"}
        </div>
        {isHitl ? (
          <Button className="bg-amber-500 hover:bg-amber-600 text-white text-sm" onClick={handleApprove} disabled={approving}>
            {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Approve &amp; Continue <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        ) : (
          <Button className="bg-violet-600 hover:bg-violet-700 text-white text-sm" onClick={() => onUpdate({ phase: "alignment" })}>
            Continue to Alignment <ArrowRight className="h-4 w-4 ml-1.5" />
            <span className="ml-1.5 text-[10px] bg-black/20 px-1.5 py-0.5 rounded-full">💎 2 credits</span>
          </Button>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   PHASE 2 — ALIGNMENT STRATEGY
   Three panels: each has chart + numbers + text
   ═══════════════════════════════════════════════ */

/* ── Panel 1: Soft Alignment Radar ─────────────── */
const SoftAlignmentRadar = ({ radar }) => {
  const [expandedDim, setExpandedDim] = useState(null);
  const DIM_KEYS = [
    { key: "mission_alignment",  label: "Mission",      short: "Mission Alignment" },
    { key: "capacity_fit",       label: "Capacity",     short: "Capacity Fit" },
    { key: "track_record_match", label: "Track Record", short: "Track Record Match" },
    { key: "network_proximity",  label: "Network",      short: "Network Proximity" },
    { key: "timing_window",      label: "Timing",       short: "Timing Window" },
  ];
  const FUNDER_IDEAL = { mission_alignment: 8.5, capacity_fit: 7.5, track_record_match: 8.0, network_proximity: 7.0, timing_window: 9.0 };

  const chartData = DIM_KEYS.map(d => ({
    dim: d.label,
    "Our Profile":    parseFloat((radar[d.key] || 0).toFixed(1)),
    "Funder Ideal":   FUNDER_IDEAL[d.key],
    fullMark: 10,
  }));

  const overallScore = parseFloat((radar.overall_fit_score || 0).toFixed(1));
  const recColor = { pursue: "#0d9488", conditional: "#d97706", defer: "#7c3aed", skip: "#dc2626" };
  const rec = radar.recommendation || "conditional";

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
        <p className="text-[13px] font-bold text-zinc-800">⭐ Soft Alignment Analysis</p>
        <div className="flex items-center gap-2">
          <span className="text-[11px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wide"
            style={{ background: recColor[rec] + "15", color: recColor[rec], borderColor: recColor[rec] + "40" }}>
            {rec}
          </span>
          <span className="text-[13px] font-black text-zinc-800">{overallScore}<span className="text-[10px] font-normal text-zinc-400">/10</span></span>
        </div>
      </div>

      {/* Chart */}
      <div className="px-4 pt-4 pb-2">
        <ResponsiveContainer width="100%" height={220}>
          <RadarChart data={chartData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
            <PolarGrid stroke="#e4e4e7" />
            <PolarAngleAxis dataKey="dim" tick={{ fontSize: 10.5, fill: "#52525b", fontWeight: 600 }} />
            <PolarRadiusAxis angle={18} domain={[0, 10]} tick={{ fontSize: 8.5, fill: "#a1a1aa" }} tickCount={3} />
            <Radar name="Our Profile"  dataKey="Our Profile"  stroke="#0d9488" fill="#0d9488" fillOpacity={0.32} strokeWidth={2} dot={{ r: 3, fill: "#0d9488" }} />
            <Radar name="Funder Ideal" dataKey="Funder Ideal" stroke="#7c3aed" fill="none"    strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 2.5, fill: "#7c3aed" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }} />
            <Tooltip formatter={(v, name) => [`${v.toFixed(1)} / 10`, name]} contentStyle={{ fontSize: "12px" }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-dimension: number + rationale text */}
      <div className="px-4 pb-4 flex flex-col gap-1.5">
        <p className="text-[10.5px] font-bold text-zinc-400 uppercase tracking-wider mb-1">Dimension Scores &amp; Rationale</p>
        {DIM_KEYS.map(d => {
          const score = parseFloat((radar[d.key] || 0).toFixed(1));
          const ideal = FUNDER_IDEAL[d.key];
          const gap = ideal - score;
          const isOpen = expandedDim === d.key;
          return (
            <div key={d.key} className="border border-zinc-100 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 transition-colors"
                onClick={() => setExpandedDim(isOpen ? null : d.key)}>
                {/* Score pill */}
                <span className="text-[13px] font-black w-9 text-right shrink-0"
                  style={{ color: score >= 7.5 ? "#0d9488" : score >= 5.5 ? "#d97706" : "#dc2626" }}>
                  {score}
                </span>
                <span className="text-[10px] text-zinc-400 shrink-0">/10</span>
                {/* Bar */}
                <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${score * 10}%`, background: score >= 7.5 ? "#0d9488" : score >= 5.5 ? "#d97706" : "#dc2626" }} />
                </div>
                <span className="text-[12px] font-semibold text-zinc-700 w-24 text-left shrink-0">{d.short}</span>
                {gap > 1.5 && <span className="text-[9.5px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0">↑ {gap.toFixed(1)} gap</span>}
                <span className="text-zinc-300 shrink-0">{isOpen ? "▲" : "▼"}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 bg-zinc-50 border-t border-zinc-100">
                  <p className="text-[12px] text-zinc-600 leading-relaxed">
                    {(radar.scoring_rationale || {})[d.key] || "No rationale available for this dimension."}
                  </p>
                  <div className="mt-2 flex items-center gap-3 text-[10.5px] text-zinc-500">
                    <span>Our score: <strong className="text-zinc-800">{score}/10</strong></span>
                    <span>Funder ideal: <strong className="text-violet-700">{ideal}/10</strong></span>
                    {gap > 0 ? <span className="text-amber-600">Gap to close: {gap.toFixed(1)}</span> : <span className="text-teal-600">✓ Meets ideal</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Panel 2: Proposal Map ──────────────────────── */
const ProposalMapPanel = ({ pm }) => {
  const [openNode, setOpenNode] = useState(null);

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
        <p className="text-[13px] font-bold text-zinc-800">🎯 Proposal Map</p>
        <span className="text-[11px] text-zinc-400">Central theme → Arguments → Evidence</span>
      </div>

      <div className="p-4 space-y-4">
        {/* Visual: radial tree as SVG */}
        <div className="relative w-full" style={{ minHeight: 260 }}>
          <svg width="100%" height="260" viewBox="0 0 560 260" className="overflow-visible">
            {/* Root → arc items connector lines */}
            {[140, 280, 420].map((x, i) => (
              <g key={i}>
                <line x1="280" y1="52" x2={x} y2="130" stroke="#c4b5fd" strokeWidth="1.5" strokeDasharray="4 3" />
                {/* arc → evidence connectors */}
                {(pm.evidence_anchors || []).filter((_, ei) => ei === i).map((_, ei) => (
                  <line key={ei} x1={x} y1="162" x2={x} y2="206" stroke="#e4e4e7" strokeWidth="1" strokeDasharray="3 3" />
                ))}
              </g>
            ))}
            {/* Root node */}
            <g style={{ cursor: "pointer" }} onClick={() => setOpenNode(openNode === "root" ? null : "root")}>
              <ellipse cx="280" cy="38" rx="112" ry="26" fill="#1e1b4b" />
              <text x="280" y="42" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">
                {(pm.central_theme || "").slice(0, 38)}
              </text>
            </g>
            {/* Secondary (narrative arc) */}
            {(pm.narrative_arc || []).map((arc, i) => {
              const cx = [140, 280, 420][i] || 280;
              const id = `arc${i}`;
              return (
                <g key={i} style={{ cursor: "pointer" }} onClick={() => setOpenNode(openNode === id ? null : id)}>
                  <ellipse cx={cx} cy="148" rx="74" ry="22" fill={openNode === id ? "#7c3aed" : "#ede9fe"} stroke="#7c3aed" strokeWidth="1.5" />
                  <text x={cx} y="152" textAnchor="middle" fill={openNode === id ? "white" : "#4c1d95"} fontSize="9.5" fontWeight="600">
                    {arc.split(":")[0].slice(0, 18)}
                  </text>
                </g>
              );
            })}
            {/* Tertiary (evidence anchors) */}
            {(pm.evidence_anchors || []).map((ev, i) => {
              const cx = [140, 280, 420][i] || 280;
              return (
                <g key={i}>
                  <ellipse cx={cx} cy="222" rx="60" ry="18" fill="#f0fdf4" stroke="#0d9488" strokeWidth="1.2" />
                  <text x={cx} y="226" textAnchor="middle" fill="#065f46" fontSize="8.5" fontWeight="600">
                    {(ev.claim || "").slice(0, 20)}
                  </text>
                </g>
              );
            })}
            {/* Legend */}
            <g>
              <ellipse cx="30" cy="246" rx="8" ry="6" fill="#1e1b4b" />
              <text x="42" y="250" fontSize="8.5" fill="#71717a">Central Theme</text>
              <ellipse cx="130" cy="246" rx="8" ry="6" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1" />
              <text x="142" y="250" fontSize="8.5" fill="#71717a">Argument</text>
              <ellipse cx="210" cy="246" rx="8" ry="6" fill="#f0fdf4" stroke="#0d9488" strokeWidth="1" />
              <text x="222" y="250" fontSize="8.5" fill="#71717a">Evidence</text>
            </g>
          </svg>
          {/* Expanded node detail overlay */}
          {openNode && openNode !== "root" && (() => {
            const idx = parseInt(openNode.replace("arc", ""));
            const arc = (pm.narrative_arc || [])[idx];
            const ev  = (pm.evidence_anchors || [])[idx];
            return arc ? (
              <div className="absolute bottom-0 left-0 right-0 bg-white border border-violet-200 rounded-xl px-3.5 py-3 shadow-lg text-[12px]">
                <p className="font-bold text-violet-800 mb-1">Argument {idx + 1}</p>
                <p className="text-zinc-700 mb-2">{arc}</p>
                {ev && <><p className="font-bold text-teal-700 mb-0.5">Evidence</p>
                  <p className="text-zinc-600"><span className="font-semibold">{ev.claim}</span> — {ev.evidence}</p>
                  <p className="text-zinc-400 text-[11px] mt-0.5">Source: {ev.source}</p></>}
              </div>
            ) : null;
          })()}
        </div>

        {/* Numbers: argument count, evidence count */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 text-center">
            <p className="text-[22px] font-black text-violet-700">{(pm.narrative_arc || []).length}</p>
            <p className="text-[10px] text-violet-500 font-medium">Core Arguments</p>
          </div>
          <div className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-center">
            <p className="text-[22px] font-black text-teal-700">{(pm.evidence_anchors || []).length}</p>
            <p className="text-[10px] text-teal-500 font-medium">Evidence Anchors</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-center">
            <p className="text-[22px] font-black text-amber-700">{(pm.key_differentiators || []).length}</p>
            <p className="text-[10px] text-amber-500 font-medium">Differentiators</p>
          </div>
        </div>

        {/* Text: Full narrative arc */}
        <div>
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-2">Narrative Arc</p>
          <ol className="space-y-2">
            {(pm.narrative_arc || []).map((arc, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <p className="text-[12.5px] text-zinc-700 leading-snug">{arc}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Text: Evidence anchors full detail */}
        <div>
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-2">Evidence Anchors</p>
          <div className="space-y-2">
            {(pm.evidence_anchors || []).map((ev, i) => (
              <div key={i} className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2.5">
                <p className="text-[12.5px] font-bold text-teal-800 mb-0.5">{ev.claim}</p>
                <p className="text-[11.5px] text-teal-700">{ev.evidence}</p>
                <p className="text-[10.5px] text-teal-500 mt-0.5">Source: {ev.source}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Text: Key differentiators */}
        <div>
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-2">Key Differentiators</p>
          <ul className="space-y-1.5">
            {(pm.key_differentiators || []).map((kd, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-zinc-700">
                <span className="text-amber-500 shrink-0 mt-0.5">◆</span>{kd}
              </li>
            ))}
          </ul>
        </div>

        {/* Text: Impact framing + format requirements */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3.5 py-3">
          <p className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Impact Framing Instruction</p>
          <p className="text-[12.5px] text-indigo-800">{pm.impact_framing}</p>
        </div>
        <div>
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-2">Format Requirements</p>
          <ul className="space-y-1">
            {(pm.format_requirements || []).map((fr, i) => (
              <li key={i} className="flex items-center gap-2 text-[12px] text-zinc-600">
                <CheckCircle2 className="h-3.5 w-3.5 text-teal-500 shrink-0" />{fr}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/* ── Panel 3: Process DAG ───────────────────────── */
const ProcessDAGPanel = ({ dag }) => {
  const [view, setView] = useState("list");
  const milestones = dag.milestones || [];
  const criticalPath = new Set(dag.critical_path || []);

  // Compute can-start-now: all depends_on IDs are in critical path (simplified: no unresolved deps)
  const canStartNow = (m) => m.depends_on.length === 0;

  const exportCSV = () => {
    const rows = [["ID","Name","Owner","Estimated Days","Depends On","Critical Path","Deliverable"]];
    milestones.forEach(m => rows.push([m.id, m.name, m.owner, m.estimated_days, m.depends_on.join("|"), criticalPath.has(m.id) ? "Yes" : "No", m.deliverable]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "process_dag.csv"; a.click();
  };

  const criticalDays = milestones.filter(m => criticalPath.has(m.id)).reduce((acc, m) => acc + m.estimated_days, 0);

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-[13px] font-bold text-zinc-800">📈 Process DAG</p>
          <span className="text-[11px] text-zinc-500">{milestones.length} steps · <span className="font-bold text-zinc-700">{dag.total_estimated_days}d total</span> · critical path <span className="font-bold text-amber-700">{criticalDays}d</span></span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-zinc-200 rounded-lg overflow-hidden text-[11px] font-semibold">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 transition-colors ${view === "list" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>List</button>
            <button onClick={() => setView("chart")} className={`px-3 py-1.5 transition-colors ${view === "chart" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>Chart</button>
          </div>
          <button onClick={exportCSV} className="text-[11px] text-zinc-500 hover:text-teal-600 border border-zinc-200 rounded-lg px-2.5 py-1.5 hover:border-teal-300 transition-colors">↓ CSV</button>
        </div>
      </div>

      {/* Critical path warning */}
      {criticalDays > 30 && (
        <div className="mx-4 mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-[12px] text-amber-800">
          <span className="text-amber-500 shrink-0 text-base">⚠</span>
          <span>Critical path is <strong>{criticalDays} days</strong> — verify this fits within your deadline window. Start letters of support immediately.</span>
        </div>
      )}

      <div className="p-4">
        {view === "list" ? (
          <div className="space-y-2">
            {milestones.map((m, i) => {
              const isCrit = criticalPath.has(m.id);
              const canStart = canStartNow(m);
              return (
                <div key={m.id} className={`rounded-xl border px-4 py-3 ${isCrit ? "border-amber-200 bg-amber-50" : "border-zinc-100 bg-white"}`}>
                  <div className="flex items-start gap-3">
                    {/* Step number */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 mt-0.5 ${isCrit ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-600"}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      {/* Row 1: name + badges */}
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <p className="text-[13px] font-bold text-zinc-800">{m.name}</p>
                        {isCrit && <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800 uppercase">Critical Path</span>}
                        {canStart && <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 uppercase">Can start now</span>}
                      </div>
                      {/* Row 2: description */}
                      <p className="text-[11.5px] text-zinc-500 leading-snug mb-1.5">{m.description}</p>
                      {/* Row 3: numbers + meta */}
                      <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-500">
                        <span className="font-bold text-zinc-700">{m.estimated_days}d</span>
                        <span>· {m.owner}</span>
                        {m.depends_on.length > 0 && <span className="text-amber-600">After: {m.depends_on.join(", ")}</span>}
                        <span className="text-teal-600">→ {m.deliverable}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Chart view: horizontal swimlane */
          <div className="overflow-x-auto">
            <div className="min-w-[480px]">
              <div className="flex items-center gap-1 text-[10px] text-zinc-400 mb-2 px-1">
                <span className="w-28 shrink-0 font-semibold">Step</span>
                <span className="flex-1 font-semibold">Timeline (relative)</span>
                <span className="w-8 text-right font-semibold">Days</span>
              </div>
              {milestones.map((m, i) => {
                const isCrit = criticalPath.has(m.id);
                const maxDays = Math.max(...milestones.map(x => x.estimated_days), 1);
                const widthPct = Math.max(8, (m.estimated_days / maxDays) * 100);
                const deps = m.depends_on;
                const depOffset = deps.length > 0
                  ? milestones.filter(x => deps.includes(x.id)).reduce((acc, x) => acc + x.estimated_days, 0)
                  : 0;
                const leftPct = Math.min(70, (depOffset / (dag.total_estimated_days || 1)) * 100);
                return (
                  <div key={m.id} className="flex items-center gap-1 mb-1.5 text-[11px]">
                    <span className="w-28 shrink-0 font-medium text-zinc-600 truncate pr-1">{m.name.slice(0, 16)}</span>
                    <div className="flex-1 relative h-7 bg-zinc-50 rounded border border-zinc-100">
                      <div className="absolute top-1 h-5 rounded flex items-center px-2"
                        style={{
                          left: `${leftPct}%`,
                          width: `${Math.min(widthPct, 100 - leftPct)}%`,
                          background: isCrit ? "#fbbf24" : "#c4b5fd",
                          color: isCrit ? "#78350f" : "#4c1d95",
                          fontSize: "9px",
                          fontWeight: 600,
                          minWidth: "20px",
                        }}>
                        {m.name.slice(0, 10)}
                      </div>
                    </div>
                    <span className="w-8 text-right font-bold text-zinc-700">{m.estimated_days}d</span>
                  </div>
                );
              })}
              <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-500">
                <span className="flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm bg-amber-400" /> Critical path</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm bg-violet-300" /> Parallel</span>
              </div>
            </div>
          </div>
        )}

        {/* Numbers summary row */}
        <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-zinc-100">
          <div className="text-center">
            <p className="text-[20px] font-black text-zinc-800">{dag.total_estimated_days}</p>
            <p className="text-[9.5px] text-zinc-400 font-medium">Total Days</p>
          </div>
          <div className="text-center">
            <p className="text-[20px] font-black text-amber-600">{criticalDays}</p>
            <p className="text-[9.5px] text-zinc-400 font-medium">Critical Path</p>
          </div>
          <div className="text-center">
            <p className="text-[20px] font-black text-teal-600">{milestones.filter(m => canStartNow(m)).length}</p>
            <p className="text-[9.5px] text-zinc-400 font-medium">Start Now</p>
          </div>
          <div className="text-center">
            <p className="text-[20px] font-black text-violet-600">{(dag.resource_requirements || []).length}</p>
            <p className="text-[9.5px] text-zinc-400 font-medium">Resources</p>
          </div>
        </div>

        {/* Text: Resource requirements */}
        {(dag.resource_requirements || []).length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Resource Requirements</p>
            <div className="flex flex-wrap gap-1.5">
              {dag.resource_requirements.map((r, i) => (
                <span key={i} className="text-[11px] bg-zinc-100 text-zinc-600 border border-zinc-200 px-2.5 py-1 rounded-full">{r}</span>
              ))}
            </div>
          </div>
        )}

        {/* Text: Risk flags */}
        {(dag.risk_flags || []).length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Risk Flags</p>
            <ul className="space-y-1.5">
              {dag.risk_flags.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <span className="text-amber-500 shrink-0">⚠</span>{r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── AlignmentPhase container ───────────────────── */
const AlignmentPhase = ({ pursuit, onUpdate }) => {
  const ad = pursuit.alignmentData || mockAlignment(pursuit);

  return (
    <div className="space-y-5">
      {/* Confidence + recommendation banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-[12px] font-bold text-violet-800 mb-0.5">Alignment Analysis Ready</p>
          <p className="text-[11.5px] text-violet-600">
            {ad.recommended_next_action}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[24px] font-black text-violet-700">{Math.round((ad.estimated_success_probability || 0) * 100)}%</p>
          <p className="text-[10px] text-violet-400 font-medium">Est. Success</p>
        </div>
      </div>

      {/* Panel 1: Soft Alignment Radar (chart + numbers + text) */}
      <SoftAlignmentRadar radar={ad.radar} />

      {/* Panel 2: Proposal Map (visual + numbers + text) */}
      <ProposalMapPanel pm={ad.proposal_map} />

      {/* Panel 3: Process DAG (chart + numbers + text) */}
      <ProcessDAGPanel dag={ad.process_dag} />

      {/* Top risks */}
      {(ad.top_risks || []).length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          <p className="text-[11px] font-bold text-red-600 uppercase tracking-wider mb-2">Top Risks to Address</p>
          {ad.top_risks.map((r, i) => (
            <p key={i} className="text-[12px] text-red-800 flex items-start gap-2 mb-1">
              <span className="text-red-400 shrink-0">•</span>{r}
            </p>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 mt-2 border-t border-zinc-100">
        <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
          <CheckCircle2 className="h-4 w-4 text-teal-500" /> Alignment reviewed — Application workflow now available
        </div>
        <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm" onClick={() => onUpdate({ phase: "application", alignmentDone: true })}>
          Continue to Application <ArrowRight className="h-4 w-4 ml-1.5" />
          <span className="ml-1.5 text-[10px] bg-black/20 px-1.5 py-0.5 rounded-full">💎 3 credits</span>
        </Button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   PHASE 3 — APPLICATION
   ═══════════════════════════════════════════════ */


const ApplicationPhase = ({ pursuit, charityBN }) => {
  const app = pursuit.applicationData || mockApplication();
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    setGenerating(true);
    // Simulated — will wire to real agent later
    setTimeout(() => {
      setDraft({
        subject: `${pursuit.grantName} \u2014 Project Narrative`,
        body: `[AI-generated draft will appear here once the application agent is connected.]\n\nOrganization requests funding from ${pursuit.funderName} to support our program. Our evidence-based model has demonstrated strong outcomes aligned with funder priorities.\n\nThis draft was prepared using alignment analysis and structure data.`,
      });
      setGenerating(false);
    }, 2000);
  };

  const stepCls = { done: "bg-emerald-50 border-emerald-100", active: "bg-teal-50 border-teal-100", pending: "bg-zinc-50 border-zinc-100" };
  const numCls = { done: "bg-emerald-500", active: "bg-teal-500", pending: "bg-zinc-300" };
  const doneCount = app.steps.filter(s => s.status === "done").length;
  const pct = Math.round((doneCount / app.steps.length) * 100);

  return (
    <div className="space-y-4">
      {/* Portal banner */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
        <p className="text-[12px] text-blue-800">🌐 <strong>External Portal Required</strong> \u2014 Submit via funder portal. Account pre-registration required.</p>
        {pursuit.applicationUrl && (
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white text-xs" onClick={() => window.open(pursuit.applicationUrl, "_blank")}>
            Open Portal <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        {/* Workflow steps */}
        <div className="flex flex-col gap-2">
          {app.steps.map((s, i) => (
            <div key={i} className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border ${stepCls[s.status]}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5 ${numCls[s.status]}`}>
                {s.status === "done" ? "\u2713" : i + 1}
              </span>
              <div className="flex-1">
                <p className={`text-[12.5px] font-semibold ${s.status === "active" ? "text-teal-700" : s.status === "done" ? "text-zinc-700" : "text-zinc-500"}`}>{s.label}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{s.sub}</p>
                {s.ai && s.status === "active" && (
                  <div className="mt-2">
                    <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white text-xs" onClick={generate} disabled={generating}>
                      {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                      Generate AI Draft
                    </Button>
                  </div>
                )}
              </div>
              <span className={`text-[10.5px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                s.status === "done" ? "bg-emerald-100 text-emerald-700" : s.status === "active" ? "bg-teal-100 text-teal-700" : "bg-zinc-100 text-zinc-500"
              }`}>{s.status === "done" ? "Done" : s.status === "active" ? "Active" : "Pending"}</span>
            </div>
          ))}
        </div>

        {/* Documents pane */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex flex-col gap-2 h-fit">
          <p className="text-[12px] font-bold text-zinc-700 mb-1">Documents</p>
          {app.documents.map((d, i) => (
            <div key={i} className="flex items-center gap-2 bg-white border border-zinc-100 rounded-md px-2.5 py-1.5">
              <span>{d.icon}</span>
              <span className="flex-1 text-[12px] text-zinc-700">{d.label}</span>
              {d.status === "done"
                ? <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[7px] shrink-0">\u2713</span>
                : d.status === "active"
                  ? <span className="w-4 h-4 rounded-full bg-teal-500 flex items-center justify-center text-white text-[8px] shrink-0">\u25cf</span>
                  : <span className="w-4 h-4 rounded-full bg-zinc-300 flex items-center justify-center text-white text-[8px] shrink-0">\u25cb</span>}
            </div>
          ))}
        </div>
      </div>

      {/* AI Draft preview */}
      <AnimatePresence>
        {draft && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-teal-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="text-[13px] font-bold text-zinc-900">\u2726 AI Draft Generated</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div>
              <p className="text-[10px] text-zinc-400 uppercase tracking-wider mb-1">Subject</p>
              <p className="text-[12.5px] font-medium text-zinc-900 bg-zinc-50 rounded-lg px-3 py-2">{draft.subject}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-400 uppercase tracking-wider mb-1">Draft</p>
              <pre className="whitespace-pre-wrap text-[12px] text-zinc-700 bg-zinc-50 rounded-lg p-3 max-h-60 overflow-y-auto font-sans leading-relaxed">{draft.body}</pre>
            </div>
            <Button size="sm" variant="outline" className="flex items-center gap-1.5 text-xs"
              onClick={() => { navigator.clipboard.writeText(draft.subject + "\n\n" + draft.body); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              <Copy className="h-3.5 w-3.5" />{copied ? "Copied!" : "Copy All"}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
        <p className="text-[12px] text-zinc-500">Application {pct}% complete</p>
        <div className="flex items-center gap-2">
          <svg width="32" height="32" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#0d9488" strokeWidth="3" strokeDasharray={`${pct} 100`} strokeDashoffset="25" transform="rotate(-90 18 18)" />
          </svg>
          <span className="text-[12px] font-bold text-teal-600">{pct}%</span>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN — FundReachTab
   ═══════════════════════════════════════════════ */

export const FundReachTab = ({ charityBN, onNavigate, storageKey = DEFAULT_STORAGE_KEY }) => {
  const key = charityBN || "demo";
  const [pursuits, setPursuits] = useState(() => loadPursuits(key, storageKey));
  const [activeId, setActiveId] = useState(null);
  const [orgData, setOrgData] = useState(null);

  // Fetch org profile for pursue API calls
  useEffect(() => {
    if (!charityBN) return;
    fetch(`/api/onboarding/lookup/${charityBN}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setOrgData(d); })
      .catch(() => {});
  }, [charityBN]);

  const makePursuit = useCallback((g) => ({
    id: (g.grant_id || "g") + "_" + Date.now(),
    grantId: g.grant_id || "unknown",
    grantName: g.program_name || g.grant_name || "Grant Program",
    funderName: g.provider_name || g.funder_name || "Funder",
    amountRange: fmtAmt(g.median_gift || g.typical_amount) || "\u2014",
    deadline: g.next_deadline || null,
    matchScore: g.final_score || g.match_score || 0,
    applicationUrl: g.application_url || null,
    province: g.province || null,
    phase: g.phase_hint === "saved" ? "saved" : "structure",
    structureDone: false,
    alignmentDone: false,
    structureData: null,
    alignmentData: null,
    applicationData: null,
    addedAt: new Date().toISOString(),
  }), []);

  // Consume pending pursuit from FundMatch "Work On"
  useEffect(() => {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    try {
      const g = JSON.parse(raw);
      localStorage.removeItem(PENDING_KEY);
      setPursuits(prev => {
        if (prev.find(p => p.grantId === g.grant_id)) return prev;
        const np = makePursuit(g);
        setActiveId(np.id);
        return [np, ...prev];
      });
    } catch {}
  }, [makePursuit]);

  // Persist
  useEffect(() => { savePursuits(key, pursuits, storageKey); }, [pursuits, key, storageKey]);

  // Auto-select first
  useEffect(() => {
    if (pursuits.length > 0 && !activeId) setActiveId(pursuits[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuits]);

  const update = (id, changes) => setPursuits(prev => prev.map(p => p.id === id ? { ...p, ...changes } : p));
  const remove = (id) => {
    if (!window.confirm("Remove this pursuit?")) return;
    setPursuits(prev => {
      const next = prev.filter(p => p.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
  };

  const active = pursuits.find(p => p.id === activeId);
  const days = active ? daysTo(active.deadline) : null;

  return (
    <div className="flex gap-4 items-start" data-testid="fundreach-tab">
      {/* ── Sidebar ── */}
      <div className="w-64 shrink-0 bg-white border border-zinc-200 rounded-xl overflow-hidden sticky top-4 self-start">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-zinc-500" />
            <span className="text-[12.5px] font-bold text-zinc-800">Pursuits</span>
            <span className="text-[10.5px] font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{pursuits.length}</span>
          </div>
        </div>
        <div className="py-2 max-h-[60vh] overflow-y-auto">
          {pursuits.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12px] text-zinc-400">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No pursuits yet.<br />Click <strong>Work On</strong> in FundMatch to add grants here.
            </div>
          ) : pursuits.map(p => (
            <PursuitRow key={p.id} pursuit={p} active={p.id === activeId} onClick={() => setActiveId(p.id)} />
          ))}
        </div>
        <div className="p-2 border-t border-zinc-100">
          <Button variant="outline" size="sm" className="w-full text-xs text-zinc-600" onClick={() => onNavigate?.("fundmatch")}>
            \u2190 Back to FundMatch
          </Button>
        </div>
      </div>

      {/* ── Workspace ── */}
      <div className="flex-1 min-w-0">
        {!active ? (
          <div className="bg-white border-2 border-teal-200 rounded-xl p-16 text-center text-zinc-400">
            <Target className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p className="text-[14px] font-semibold text-zinc-600 mb-1">No pursuit selected</p>
            <p className="text-[12.5px]">Select a pursuit from the sidebar or return to FundMatch to add grants.</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Frame header */}
            <div className="bg-white border-2 border-teal-500 border-b-0 rounded-t-xl px-5 py-4">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <select className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-[13px] font-bold text-zinc-800 bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-teal-300"
                    value={activeId} onChange={e => setActiveId(e.target.value)}>
                    {pursuits.map(p => <option key={p.id} value={p.id}>{p.grantName} \u2014 {p.funderName}</option>)}
                  </select>
                </div>
                {active.deadline && (
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold ${dlCls(days)}`}>
                    <Calendar className="h-3.5 w-3.5" />
                    {fmtDate(active.deadline)}{days != null && days > 0 ? ` \u00b7 ${days}d` : ""}
                  </div>
                )}
                <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5 shrink-0">
                  <div>
                    <p className="text-[9px] font-bold text-violet-400 uppercase tracking-wider">Confidence</p>
                    <p className="text-[18px] font-black text-violet-700 leading-none">
                      {Math.round((active.matchScore || 0) * 100)}<span className="text-[11px] font-medium text-violet-400">/100</span>
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-red-400 shrink-0 ml-auto p-1" onClick={() => remove(active.id)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-4 flex-wrap text-[12px] text-zinc-600">
                <span>🏛 <strong>{active.funderName}</strong></span>
                {active.amountRange && active.amountRange !== "\u2014" && <span>💰 <strong>{active.amountRange}</strong></span>}
                {active.province && <span>📍 <strong>{active.province}</strong></span>}
                <span className="bg-violet-100 text-violet-700 text-[10.5px] font-bold px-2 py-0.5 rounded-full">
                  {Math.round((active.matchScore || 0) * 100)}% Match
                </span>
              </div>
            </div>

            {/* Phase tabs */}
            <div className="flex border-2 border-teal-500 border-t-0 border-b-0 bg-zinc-50">
              {["structure", "alignment", "application"].map(ph => (
                <PhaseTab key={ph} phase={ph} current={active.phase} sDone={active.structureDone} aDone={active.alignmentDone}
                  onClick={() => update(active.id, { phase: ph })} />
              ))}
            </div>

            {/* Phase content */}
            <div className="border-2 border-teal-500 border-t-0 rounded-b-xl bg-white p-5">
              <AnimatePresence mode="wait">
                {active.phase === "saved" && (
                  <motion.div key="saved" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <div className="p-8 text-center space-y-4">
                      <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto">
                        <BookmarkCheck className="h-7 w-7 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-zinc-900">Grant Saved — Ready to Process</h3>
                        <p className="text-sm text-zinc-500 mt-1">
                          {active.grantName} has been saved from FundMatch. Start the Structure phase to extract
                          funder requirements, deadlines, and required documents.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={() => {
                            setPursuits(prev => prev.map(p => p.id === active.id ? { ...p, phase: "structure" } : p));
                          }}
                        >
                          Begin Structure →
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setPursuits(prev => prev.filter(p => p.id !== active.id));
                            setActiveId(null);
                          }}
                        >
                          Remove from List
                        </Button>
                      </div>
                      <div className="text-xs text-zinc-400 space-y-1">
                        <p>Grant: {active.grantName}</p>
                        <p>Funder: {active.funderName}</p>
                        {active.matchScore > 0 && <p>Match Score: {Math.round(active.matchScore * 100)}%</p>}
                      </div>
                    </div>
                  </motion.div>
                )}
                {active.phase === "structure" && (
                  <motion.div key="s" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <StructurePhase pursuit={active} charityBN={charityBN} orgData={orgData} onUpdate={c => update(active.id, c)} />
                  </motion.div>
                )}
                {active.phase === "alignment" && (
                  <motion.div key="a" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <AlignmentPhase pursuit={active} onUpdate={c => update(active.id, c)} />
                  </motion.div>
                )}
                {active.phase === "application" && (
                  <motion.div key="p" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <ApplicationPhase pursuit={active} charityBN={charityBN} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundReachTab;
